import { UserContext } from './user-context.js';
import { object } from '../typing/object.js';
import { string } from '../typing/string.js';
import { record } from '../typing/record.js';
import { literal } from '../typing/literal.js';
import { union } from '../typing/union.js';
import { optional } from '../typing/optional.js';
import { number } from '../typing/number.js';
import { validate } from '../typing/validate.js';
import { TypeOf } from '../typing/type-of.js';

export const PermissionErrorPartition = object('PermissionErrorPartition', {
  tenant: string(),
  realm: string(),
  version: string(),
  pattern: string(),
  name: string(),
  run: number(),
});

const PermissionDetailStateRead = object('PermissionErrorDetailStateRead', {
  kind: literal('state-read'),
  stateName: string(),
});

const PermissionDetailEventWrite = object('PermissionErrorDetailEventWrite', {
  kind: literal('event-write'),
  eventName: string(),
});

const PermissionDetailEventRead = object('PermissionErrorDetailEventRead', {
  kind: literal('event-read'),
  eventName: string(),
  storedEventId: string(),
});

const PermissionDetailStateReadPolicy = object(
  'PermissionErrorDetailStateReadPolicy',
  {
    kind: literal('state-read-policy'),
    stateName: string(),
    terminal: string(),
    indexName: optional(string()),
    indexBindings: optional(record(string(), string())),
  },
);

export const PermissionErrorDetailType = union(
  'PermissionErrorDetail',
  [
    PermissionDetailStateRead,
    PermissionDetailEventWrite,
    PermissionDetailEventRead,
    PermissionDetailStateReadPolicy,
  ],
  'kind',
);

export type PermissionErrorDetail = TypeOf<typeof PermissionErrorDetailType>;

export const PermissionErrorPayloadType = object('PermissionErrorPayload', {
  partition: PermissionErrorPartition,
  context: UserContext,
  detail: PermissionErrorDetailType,
});

export type PermissionErrorPayload = TypeOf<typeof PermissionErrorPayloadType>;

export const RpcPermissionDeniedBody = object('RpcPermissionDeniedBody', {
  message: string(),
  data: PermissionErrorPayloadType,
});

export function permissionErrorFromBody(body: unknown): {
  partition: unknown;
  context: unknown;
  detail: PermissionErrorDetail;
} | null {
  const [err, parsed] = validate(body, RpcPermissionDeniedBody);
  if (err || !parsed) return null;
  return {
    partition: parsed.data.partition,
    context: parsed.data.context,
    detail: parsed.data.detail,
  };
}
